/**
 * **Clear** — the homebrew seventh sitrep, and the only one whose predicate reads
 * terrain (rather than occupancy or a tracked combatant). A size-4
 * terraforming rig bulldozes a landing zone flat while hostiles pour in; the fight
 * ends when the ground is flat, or when the rig dies trying.
 *
 * **Branch order (checked in this priority):**
 *   1. **PC win** — every `clear`-kind zone is clear (see {@link sitrepClearance}
 *      for the three obstruction readings and their exclusions), over at least one
 *      real cell.
 *   2. **NPC win** — `sitrep.objectiveId` names the rig and the rig is destroyed or
 *      gone from the field. Losing the rig is a real fail state, and the engine calls
 *      it, not the GM.
 *   3. **active** — otherwise.
 *
 * **Why PC win is checked FIRST — ruled by the developer. Do not "fix" this
 * ordering.** Zone clear + rig dead in the same resolution is a **WIN**: the job
 * finished, so the ship lands, and the rig dying at the last second does not
 * retroactively undo a completed objective.
 *
 * Worth guarding in a comment because two things in this codebase point the other way.
 * First, losing the rig is described as a real fail state, which reads like the loss
 * should dominate. Second, the only sibling predicate with the same shape — {@link
 * "./extraction" extractionStatus} — checks its **draw first**, deliberately, so a
 * destroyed objective can never also read as extracted. The asymmetry is intentional:
 * Escort's Objective is *the thing that had to survive*, so its destruction is the whole
 * outcome; the rig is a **tool**, and a tool breaking after the work is done doesn't
 * unmake the work. In practice the two arms are near disjoint anyway — only the rig's
 * sweep clears ground, so it cannot both finish the job and already be dead.
 *
 * **No round cap — deliberately.** Enemy waves are endless until the zone
 * is clear; the clearance predicate *is* the whole clock. So `sitrep.roundLimit` is
 * carried on the config (the field is required by {@link SitrepConfig}, and
 * `seedSitrep` emits a nominal value) but this predicate never reads it, and the
 * returned status carries **no `roundsRemaining`** — there is no countdown to show.
 * That is the single biggest behavioural difference from the six printed sitreps,
 * every one of which loses to the clock.
 *
 * **A zero-cell objective is never a win.** No `clear`-kind zones authored, empty
 * rects, or a hex-mode encounter all report `total === 0`, and the win arm requires
 * `total > 0` — otherwise `every([])`-style vacuity would hand out a victory the
 * instant a GM typed `sitrep set clear` and before authoring any geometry at all.
 */
import { isAble, type EncounterState, type SitrepConfig } from "@lancer-vtt/domain";
import type { SitrepStatus } from "../status";
import { sitrepClearance } from "../clearance";

export function clearStatus(state: EncounterState, sitrep: SitrepConfig): SitrepStatus {
  const clearance = sitrepClearance(state, sitrep);

  if (clearance.total > 0 && clearance.obstructed === 0) {
    return { phase: "decided", winner: "pc" };
  }

  if (sitrep.objectiveId !== undefined) {
    const rig = state.combatants[sitrep.objectiveId];
    // Destroyed OR removed from the field. Unlike Escort/Extraction this is a LOSS,
    // not a draw: nothing else on the board can flatten the ground, so the fight is
    // unwinnable from here and calling it a draw would flatter the outcome.
    if (!rig || !isAble(rig)) return { phase: "decided", winner: "npc" };
  }

  return { phase: "active" };
}
